/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import React, { useState } from "react"; // Importing React and hooks useEffect and useState
import { useDispatch, useSelector } from "react-redux"; // Importing Redux hooks to dispatch actions and access the state
import CustomTh from "../../../../../components/table/CustomTh"; // Custom component for table header cells
import CustomTd from "../../../../../components/table/CustomTd"; // Custom component for table body cells
import { DownloadProviderImage } from "../../../../../reduxToolkit/Providers/providersApi"; // Redux action to download a provider image
import DropDownMenuProfile from "../../../../../components/DropDown/DropDownMenuProfile"; // Component for dropdown menu in each row
import DropDownMenuItem from "../../../../../components/DropDown/DropDownMenuItem"; // Component for menu item in the dropdown menu
import DownloadIcon from "@mui/icons-material/Download"; // Download icon from Material UI
import TableFunction from "../../../../../components/table/TableFunction"; // Custom table functions for filtering, pagination, etc.
import CustomImageTable from "../../../../../components/CustomImageTable"; // Custom component for displaying images in the table
import {EMPTY_VALUES, getImageByExt} from "../../../../../Apis/functions";
import {SEARCH_MODEL} from "../../../../../components/table/Model/TableModel";
import {SimpleSearch} from "../../../../../reduxToolkit/Search/SearchApi";
import {TABLES} from "../../../../../Apis/Tables";
import {permissionObj} from "../../../../../Helpers/permission";
import {ACTION_MODEL} from "../../../../../Helpers/ActionModel";
import {DetailsIconsOptions, UpdateIconsOptions} from "../../../../../components/buttons/IconButtons"; // Function to get the image based on the file extension

// Main component for the employee documents table
const EmployeeDocsTableApp = ({ setSelectedList, modified, setModified, setIsAllChecked, handleAction}) => {
    const dispatch = useDispatch(); // Using Redux's dispatch hook to send actions
    const [tableId, setTableId] = useState("tbl_edt_employee_docs"); // State for table ID
    const [searchId, setSearchId] = useState("txt_edt_search_employee_docs"); // State for search field ID
    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Accessing the global development state
    const { permission } = useSelector((state) => state.authenticatioauthennSlice); // Accessing the user's permissions
    const [bodyForExport, setBodyForExport] = useState(''); // State for the data to be exported
    const [results, setResults] = useState([]); // State to store the fetched document results
    const option = `${TABLES.EMPLOYEE_DOCUMENT_COMPANY}`;
    const [searchElement, setSearchElement] = useState(''); // Search filter value
    const [simpleSearch, setSimpleSearch] = useState(false); // Simple search flag
    const [search, setSearch] = useState(false); // Search flag


    // Function to return the table headers (thead)
    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={50} classes="first_head">{t('document_name')}</CustomTh>, // Document name column
            <CustomTh tableId={tableId}>{t('department_name')}</CustomTh>, // Department name column
            <CustomTh tableId={tableId}>{t('document')}</CustomTh> // Document file column
        ];
    };

    // Function to return the table rows (tbody)
    const getTbody = () => {
        if (!results || results?.content?.length === 0) return {
            body: [],
            data: []
        }; // If no results, return an empty table

        let body = results?.content?.map((item, index) => {
            return (
                <>
                    <CustomTd tableId={tableId} fixed={true} left={50} classes={"first-column"}>
                        <span className="text-ellipsis" title={item?.document}>
                            {item?.document} {/* Document name */}
                        </span>
                    </CustomTd>
                    <CustomTd tableId={tableId}>{item?.department?.name || item?.departmentName || EMPTY_VALUES.DEFAULT}</CustomTd> {/* Department name */}
                    <CustomTd tableId={tableId}>
                        {item?.path ? (
                            <CustomImageTable tooltip={item?.path} image={getImageByExt(item?.path?.split('.').pop())} placement={"top"}>
                                <span className={"text-ellipsis ml-1 vertical-align-middle"}>{item?.path}</span>
                            </CustomImageTable>
                            // If there is a file, show its corresponding image based on the file extension
                        ) : (
                            EMPTY_VALUES.DEFAULT // If there is no file, display '--'
                        )}
                    </CustomTd>
                </>
            );
        });

        return {
            body: body,
            data: results?.content
        };
    };

    // Function to return the action buttons (e.g., download button) for each row
    const getActionsButtons = (item, index) => {
        return <>
            <DropDownMenuProfile
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                menuId={`menu-${item.id}`}
            >
                {item?.path &&
                    <DropDownMenuItem
                        id={"btn_edt_download_" + item.id}
                        index={index}
                        handleClose={handleClose}
                        name={t('download')}
                        icon={<DownloadIcon/>}
                        click={() => {
                            dispatch(
                                DownloadProviderImage({
                                    id: item?.id, // Document ID
                                    option: TABLES.EMPLOYEE_DOCUMENT_COMPANY, // Option to download employee document
                                    type: item?.path, // File path
                                })
                            ); // Dispatch the download action
                        }}
                    />
                }

                {(DEV || permission?.includes(permissionObj?.WEB_EMPLOYEE_DOCUMENT_UPDATE)) &&
                <DropDownMenuItem
                    id={"btn_edt_update_"+ item.id}
                    index={index}
                    handleClose={handleClose}
                    name={t('update')}
                    icon={<UpdateIconsOptions />}
                    click={() => handleAction(item, ACTION_MODEL.UPDATE)}
                />
                }

                <DropDownMenuItem
                    id={"btn_edt_details_"+ item.id}
                    index={index}
                    handleClose={handleClose}
                    name={t('details')}
                    icon={<DetailsIconsOptions />}
                    click={() => handleAction(item, ACTION_MODEL.READ)}
                />
            </DropDownMenuProfile>
        </>;
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({page, size:rowsPerPage, model:option, key:searchElement })
        setBodyForExport(body);
        dispatch(SimpleSearch({ body })).then(
            ({payload: {data: { data, success }}}) => {
                if (success === true) {
                    setResults(data);
                }
            },
        );
    }

    // Using the TableFunction helper to generate the table elements
    const { t, thead, tbody, handleCheckboxChange,
        openMenu, handleMenuToggle, handleClose, handleDeleteAll, pagination, page, rowsPerPage } = TableFunction({
        setSelectedList: setSelectedList, // Callback to set the selected rows
        data: results, // Table data
        getThead, // Function to generate the table headers
        getTbody, // Function to generate the table body
        setIsAllChecked,
        fixedPagination:false,
        hasContainer:true,
        labelRowsPerPage: 'documentation_per_page',
        searchElement,//New
        getData,//New
        modified,//New
        setModified,//New
    });

    return {
        DEV,
        permission,
        tableId,
        searchId,
        thead,
        tbody,
        getActionsButtons,
        handleCheckboxChange,
        handleDeleteAll,
        pagination,
        page,
        rowsPerPage,
        setBodyForExport,
        results,
        setResults,
        searchElement,
        setSearchElement,
        option,
        simpleSearch,
        setSimpleSearch,
        search,
        setSearch
    };

}

export default EmployeeDocsTableApp; // Exporting the EmployeeDocsTableApp component
